import { CommonModule } from "@angular/common";
import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { AuthorisationStatusDirective } from "../../../directives/authorisation-status-color.directive";
import { FullPageMaskService, SpinnerModule } from "common";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { Subject, debounceTime, distinctUntilChanged, lastValueFrom, takeUntil } from "rxjs";
import { AccountApiService } from "../../../services/account-api.service";
import { OPAStatusDesc } from "../../../interfaces/opa-status-desc.interface";
import { OpaItemWithSenderIdList } from "../../../interfaces/opa-item-with-sender-id-list.interface";
import { OPAStatus } from "../../../interfaces/opa-status.interface";
import { OpaAuthorisationItem } from "../../../interfaces/opa-authorisation-item.interface";
import { authorisedOpasFilterPredicate } from "./authorised-sender-ids-dialog-predicate";
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import { TableConstants } from "../../../constants/table.constant";
import { OpaAuthorisationStatusDataItem } from "../../../interfaces/opa-authorisation-status-data-item.interface";
import { AuthorisedSenderIdsFormGroup } from "../../../interfaces/forms/authorised-sender-ids-form.interface";

export interface AuthorisedSenderIdsDialogData {
  opa: OpaItemWithSenderIdList;
}


@Component({
  selector: 'si-authorised-opas-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatTableModule, CommonModule, MatIconModule, ReactiveFormsModule, MatFormField, MatLabel, MatInputModule, AuthorisationStatusDirective, SpinnerModule, MatSlideToggleModule, MatPaginatorModule],
  templateUrl: './authorised-sender-ids-dialog.component.html',
  styleUrls: ['./authorised-sender-ids-dialog.component.scss'],
})
export class AuthorisedSenderIdsDialogComponent implements OnInit {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  public readonly columns = ['senderIds', 'actions'];

  private _destroy$ = new Subject<void>();

  private readonly maskService = inject(FullPageMaskService);
  private readonly dialogRef = inject(MatDialogRef<AuthorisedSenderIdsDialogComponent>);
  private readonly accountApiService = inject(AccountApiService);
  public data = inject<AuthorisedSenderIdsDialogData>(MAT_DIALOG_DATA);
  public readonly fb = inject(FormBuilder);

  public OPAStatusDesc = OPAStatusDesc;
  public TableConstants = TableConstants;
  public isFetching = true;
  public formArray: FormArray<FormGroup<AuthorisedSenderIdsFormGroup>> = this.fb.array<FormGroup<AuthorisedSenderIdsFormGroup>>([]);
  public filter: FormControl = new FormControl('');
  public dataSource = new MatTableDataSource<OpaAuthorisationItem>();

  public async ngOnInit(): Promise<void> {
    this.maskService.showLoading();

    try {
      const data = await lastValueFrom<OpaAuthorisationItem[]>(this.accountApiService.getOpaSenderIds$(this.data.opa.opaAccountID));

      data.forEach(({ statusDesc, senderIdID, authorisationId }) => this.formArray.push(this.fb.group({
        senderIdID: new FormControl({ disabled: true, value: senderIdID }),
        authorisationId: new FormControl({ disabled: true, value: authorisationId }),
        status: new FormControl(statusDesc === OPAStatusDesc.Authorised),
      }), { emitEvent: false }));

      this.dataSource = new MatTableDataSource<OpaAuthorisationItem>(data);
      this.dataSource.filterPredicate = authorisedOpasFilterPredicate;
      this.dataSource.paginator = this.paginator;
    } finally {
      this.maskService.hideLoading();
      this.isFetching = false;
    }

    this.addSubscription();
  }

  private addSubscription() {
    this.filter.valueChanges.pipe(
      takeUntil(this._destroy$),
      debounceTime(500),
      distinctUntilChanged(),
    )
      .subscribe(value => {
        this.applyFilter(value);
      });
  }

  private applyFilter(value: string) {
    this.dataSource.filter = value.trim().toLowerCase();
    this.dataSource.paginator.firstPage();
  }

  public get disableSaveButton(): boolean {
    return this.formArray.controls.every((control) => control.get('status').pristine);
  }

  public getFormArrayControl(element: OpaAuthorisationItem) {
    return this.formArray.controls
      .find(
        (control) => control.get('senderIdID').value === element.senderIdID &&
          control.get('authorisationId').value === element.authorisationId)
      .get('status');
  }

  public getFormControlAt(index: number) {
    return this.formArray.at(index).get('status');
  }

  public handleClose() {
    this.dialogRef.close();
  }

  public handleFormSubmit() {
    const parsedData = this.formArray
      .getRawValue()
      .filter((_, index) => this.getFormControlAt(index).dirty)
      .map<OpaAuthorisationStatusDataItem>(({ status, ...data }) => ({
        ...data,
        statusId: status ? OPAStatus.Authorised : OPAStatus.NotAuthorised,
      }));

    this.dialogRef.close(parsedData);
  }
}
